import { EmptyState } from '@components/DataTable';
import Icons from '@components/Icons';
import Pagination from '@components/Pagination';
import FeatureCreditsDisabler, { useHasFeatureCredits } from '@modules/features/FeatureCreditsDisabler';
import { SectionCard } from '@modules/talents/components/SectionCard';
import {
    Button,
    Card,
    CardActions,
    CardContent,
    CircularProgress,
    Grid,
    Skeleton,
    Stack,
    styled,
} from '@mui/material';
import { FeatureNames } from '@utils/permissions';
import { Roles } from '@utils/roleUtils';
import React from 'react';
import { useParams } from 'react-router-dom';
import OrganizationUserCard from '../components/OrganizationUserCard';
import { PopUpMenu } from '../components/PopUpMenu';

// ─── Skeleton ─────────────────────────────────────────────────────────────────

const OrganizationUserCardSkeleton = () => (
    <SkeletonCard variant="outlined">
        <SkeletonCardTopContent>
            <Stack flexDirection="row" alignItems="flex-start" justifyContent="flex-start" gap="24px" width="100%">
                <Skeleton variant="rounded" width={55} height={55} />
                <Stack direction="column" flex={1} gap={1}>
                    <Skeleton variant="text" width="60%" height={24} />
                    <Skeleton variant="text" width="80%" height={20} />
                </Stack>
                <Skeleton variant="rounded" width={80} height={32} />
            </Stack>
        </SkeletonCardTopContent>
        <SkeletonCardBottomContent>
            <Stack flexDirection="row" gap={2}>
                <Stack gap={0.5}>
                    <Skeleton variant="text" width={100} height={20} />
                    <Skeleton variant="text" width={120} height={24} />
                </Stack>
                <Stack gap={0.5}>
                    <Skeleton variant="text" width={140} height={20} />
                    <Skeleton variant="rounded" width={80} height={24} />
                </Stack>
            </Stack>
        </SkeletonCardBottomContent>
        <SkeletonCardActions>
            <Stack gap={1} flexDirection="row" alignItems="center">
                <Skeleton variant="circular" width={24} height={24} />
                <Skeleton variant="text" width={80} height={20} />
            </Stack>
        </SkeletonCardActions>
    </SkeletonCard>
);

// ─── Props ────────────────────────────────────────────────────────────────────

export interface OrganizationUsersDataGridProps {
    proId: string;
    users: any[];
    loading: boolean;
    isActionLoading: boolean;
    meta: {
        totalDocs: number;
        totalPages: number;
        hasNextPage: boolean;
        hasPrevPage: boolean;
    };
    pagination: { offset: number; limit: number };
    onPaginationChange: (pagination: { offset: number; limit: number }) => void;
    onEdit: (item: any) => void;
    onDelete: (id: string) => void;
    onResendInvitation: (id: string) => void;
    onUpdateStatus: (item: any) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

const OrganizationUsersDataGrid = ({
    proId,
    users,
    loading,
    isActionLoading,
    meta,
    pagination,
    onPaginationChange,
    onEdit,
    onDelete,
    onResendInvitation,
    onUpdateStatus,
}: OrganizationUsersDataGridProps) => {
    const hasCredits = useHasFeatureCredits([FeatureNames.ADMIN_USERS_CREDITS, FeatureNames.RECRUITER_USERS_CREDITS]);

    return (
        <React.Fragment>
            <Grid container spacing={2}>
                {loading && users.length === 0 ? (
                    Array.from({ length: 6 }).map((_, index) => (
                        <Grid item key={`skeleton-${index}`} lg={4} md={6} xs={12}>
                            <OrganizationUserCardSkeleton />
                        </Grid>
                    ))
                ) : (
                    (users ?? []).map((item, index) => (
                        <Grid item key={`${index}-${item._id}`} lg={4} md={6} xs={12}>
                            <OrganizationUserCard
                                item={item}
                                showSubordinates={
                                    item.role === Roles.ADMIN_PRO &&
                                    (hasCredits || item.totalSubordinates > 0)
                                }
                                actionButtons={() => (
                                    <>
                                        {isActionLoading ? (
                                            <Stack alignItems="center" justifyContent="center" width={24}>
                                                <CircularProgress size={20} color="secondary" />
                                            </Stack>
                                        ) : (
                                            <PopUpMenu
                                                key={item._id}
                                                id={item._id}
                                                button={<Icons name="menu-dots-vertical" />}
                                                editHref={`/companies/${proId}/users/${item._id}/edit`}
                                                onDelete={() => onDelete(item._id ?? '')}
                                                onResendInvitation={() => onResendInvitation(item._id ?? '')}
                                                onUpdateStatus={() => onUpdateStatus(item)}
                                                labelActive={!item?.isMemberActive ? 'Activer' : 'Désactiver'}
                                            />
                                        )}
                                    </>
                                )}
                            />
                        </Grid>
                    ))
                )}
            </Grid>

            {(users ?? []).length > 0 && (
                <PaginationWrapper>
                    <Pagination
                        loading={loading}
                        hasNext={!!meta?.hasNextPage}
                        hasPrev={!!meta?.hasPrevPage}
                        onChange={p => onPaginationChange({ offset: p.offset, limit: p.limit })}
                        data={{ offset: pagination.offset, limit: pagination.limit }}
                        currentPage={pagination.offset / pagination.limit + 1}
                        totalPages={meta?.totalPages}
                    />
                </PaginationWrapper>
            )}

            {(users ?? []).length === 0 && !loading && (
                <EmptyState title="Vous n'avez pas encore créé d'équipe, ajoutez votre premier membre maintenant" />
            )}
        </React.Fragment>
    );
};

// ─── Styled Components ────────────────────────────────────────────────────────

const PaginationWrapper = styled(Stack)(({ theme }) => ({
    marginTop: theme.spacing(3),
}));

const SkeletonCard = styled(Card)(() => ({
    height: '100%',
    minHeight: 216,
    width: '100%',
}));

const SkeletonCardTopContent = styled(CardContent)(({ theme }) => ({
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    paddingBottom: 0,
}));

const SkeletonCardBottomContent = styled(CardContent)(({ theme }) => ({
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
}));

const SkeletonCardActions = styled(CardActions)(({ theme }) => ({
    backgroundColor: theme.palette.transparentPurple.main,
    justifyContent: 'space-between',
    alignItems: 'center',
    height: 40,
}));

export default OrganizationUsersDataGrid;
